import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stars, BookOpen, Heart, Briefcase, Moon, Sparkles, Share2, Save, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';

const DivineGuidance = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('astrology'); // astrology, scripture
  const [astrologyFocus, setAstrologyFocus] = useState(null); // null until user selects
  const [dailyReading, setDailyReading] = useState(null);
  const [zodiacInfo, setZodiacInfo] = useState(null);
  const [scriptureGuidance, setScriptureGuidance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userMood, setUserMood] = useState(null); // User must select mood first
  const [hasLoadedZodiac, setHasLoadedZodiac] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Load zodiac info once when component mounts (doesn't require selection)
  useEffect(() => {
    if (user?.date_of_birth && !hasLoadedZodiac && activeSection === 'astrology') {
      loadZodiacInfo();
      setHasLoadedZodiac(true);
    }
  }, [user, activeSection]);

  const loadZodiacInfo = async () => {
    try {
      const zodiacResponse = await fetch(`${API_BASE_URL}/api/astrology/zodiac-info/${user.date_of_birth}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (zodiacResponse.ok) {
        const zodiacData = await zodiacResponse.json();
        setZodiacInfo(zodiacData);
      }
    } catch (error) {
      console.error('Error loading zodiac info:', error);
    }
  };

  const loadAstrologyGuidance = async (focusId = null) => {
    const selectedFocus = focusId || astrologyFocus;
    if (!selectedFocus) {
      console.log('No astrology focus selected yet');
      return;
    }
    
    setLoading(true);
    try {
      // Load daily reading based on selected focus
      const readingResponse = await fetch(`${API_BASE_URL}/api/astrology/daily-reading`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          birth_date: user.date_of_birth,
          focus_area: selectedFocus
        }),
      });

      if (readingResponse.ok) {
        const readingData = await readingResponse.json();
        setDailyReading(readingData);
        
        toast({
          title: "✨ Divine Guidance Received",
          description: `Your ${selectedFocus} reading is ready`,
          duration: 3000,
        });
      } else {
        const errorData = await readingResponse.json().catch(() => ({}));
        toast({
          title: "Unable to Load Reading",
          description: errorData.detail || "Please try again",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error loading astrology:', error);
      toast({
        title: "Connection Error",
        description: "Unable to load astrology reading",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadScriptureGuidance = async (moodId = null) => {
    const selectedMood = moodId || userMood;
    if (!selectedMood) {
      console.log('No mood selected yet');
      return;
    }
    
    setLoading(true);
    try {
      // Call dedicated scripture endpoint
      const response = await fetch(`${API_BASE_URL}/api/scripture/guidance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mood: selectedMood
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setScriptureGuidance(data);
        
        toast({
          title: "📖 Scripture Received",
          description: `God's word for your ${selectedMood} heart`,
          duration: 3000,
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast({
          title: "Unable to Load Scripture",
          description: errorData.detail || "Please try again",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error loading scripture:', error);
      toast({
        title: "Connection Error",
        description: "Unable to load scripture guidance",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveToLoveVault = async (content, type) => {
    try {
      // Save guidance to Love Vault
      toast({
        title: "✨ Saved to Love Vault",
        description: `Your ${type} guidance is safely stored for reflection 💕`,
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Save Failed",
        description: "Unable to save guidance",
        variant: "destructive",
      });
    }
  };

  const shareToConchi = async (content, type) => {
    try {
      // Start conversation with Conchi about the guidance
      toast({
        title: "💖 Shared with Conchi",
        description: `Conchi is ready to discuss your ${type} guidance`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Error sharing with Conchi:', error);
    }
  };

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-rose-50 via-white to-rose-100" style={{paddingTop: '64px'}}>
      <div className="max-w-4xl mx-auto px-4 pb-4">
        
        {/* Header */}
        <div className="flex items-center space-x-2 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-white/80 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-serif font-bold text-gray-900">Divine Guidance</h1>
            <p className="text-gray-600 text-xs">Cosmic wisdom for your journey</p>
          </div>
        </div>

        {/* Section Toggle */}
        <div className="flex justify-center mb-8">
          <div className="flex space-x-1 bg-white/80 backdrop-blur-sm p-1 rounded-xl border-2" style={{ borderColor: '#F7C6D9' }}>
            <button
              onClick={() => setActiveSection('astrology')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all font-serif ${
                activeSection === 'astrology'
                  ? 'bg-rose-500 text-white shadow-lg'
                  : 'text-gray-600 hover:text-white hover:bg-rose-500'
              }`}
            >
              <Stars className="w-4 h-4" />
              <span>Astrology</span>
            </button>
            <button
              onClick={() => setActiveSection('scripture')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all font-serif ${
                activeSection === 'scripture'
                  ? 'bg-rose-500 text-white shadow-lg'
                  : 'text-gray-600 hover:text-white hover:bg-rose-500'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              <span>Scripture</span>
            </button>
          </div>
        </div>

        {/* Astrology Section */}
        {activeSection === 'astrology' && (
          <div className="space-y-6">
            
            {/* Missing Birth Date Prompt */}
            {!user?.date_of_birth && (
              <Card className="p-8 bg-white/90 backdrop-blur-sm border-2" style={{ borderColor: '#F7C6D9' }}>
                <div className="text-center">
                  <div className="text-6xl mb-4">🌟</div>
                  <h3 className="text-xl font-serif font-bold text-gray-900 mb-4">Unlock Your Cosmic Blueprint</h3>
                  <p className="text-gray-600 mb-6 font-sans">
                    Add your birth date to receive personalized cosmic guidance for your transformation journey.
                  </p>
                  <Button 
                    asChild
                    className="text-white rounded-full font-sans"
                    style={{ backgroundColor: '#F7C6D9' }}
                  >
                    <a href="/app/profile">
                      ✨ Add Birth Date in Profile
                    </a>
                  </Button>
                </div>
              </Card>
            )}

            {/* Astrology Focus Tabs */}
            {user?.date_of_birth && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="flex space-x-1 bg-white/70 p-1 rounded-lg border" style={{ borderColor: '#F7C6D9' }}>
                    {[
                      { id: 'general', name: 'General', icon: '🌙' },
                      { id: 'love', name: 'Love', icon: '💖' },
                      { id: 'career', name: 'Career', icon: '💼' }
                    ].map((focus) => (
                      <button
                        key={focus.id}
                        onClick={() => {
                          setAstrologyFocus(focus.id);
                          // Clear previous reading and immediately load new one
                          setDailyReading(null);
                          // Trigger generation immediately after selection
                          setTimeout(() => loadAstrologyGuidance(focus.id), 100);
                        }}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-md font-medium transition-all font-sans ${
                          astrologyFocus === focus.id
                            ? 'bg-rose-500 text-white shadow-md'
                            : 'text-gray-600 hover:text-white hover:bg-rose-500'
                        }`}
                      >
                        <span>{focus.icon}</span>
                        <span>{focus.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Zodiac Overview */}
                {zodiacInfo && (
                  <Card className="p-6 bg-white/90 backdrop-blur-sm border-2 mb-6" style={{ borderColor: '#F7C6D9' }}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-2xl font-serif font-bold text-gray-900">
                          Your Sign: {zodiacInfo.zodiac_sign} ✨
                        </h2>
                        <p className="text-gray-600 font-sans">
                          Born {user.date_of_birth ? new Date(user.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : 'N/A'}
                        </p>
                      </div>
                      <div className="text-6xl">
                        {zodiacInfo.symbol || '⭐'}
                      </div>
                    </div>
                  </Card>
                )}

                {/* Daily Reading */}
                {loading && (
                  <Card className="p-8 bg-white/90 backdrop-blur-sm border-2" style={{ borderColor: '#F7C6D9' }}>
                    <div className="text-center py-12">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-rose-500 border-t-transparent mb-4"></div>
                      <p className="text-gray-600 font-sans">Loading your cosmic guidance...</p>
                    </div>
                  </Card>
                )}
                
                {!loading && dailyReading && (
                  <Card className="p-8 bg-white/90 backdrop-blur-sm border-2" style={{ borderColor: '#F7C6D9' }}>
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                        Today's {astrologyFocus === 'general' ? 'Cosmic' : astrologyFocus === 'love' ? 'Love' : 'Career'} Guidance
                      </h3>
                      <p className="text-gray-600 font-sans">
                        {new Date().toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>

                    {/* Reading Content */}
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-purple-50 to-rose-50 rounded-xl p-6 border" style={{ borderColor: '#F7C6D9' }}>
                        <h4 className="font-serif font-semibold text-gray-900 mb-3 flex items-center">
                          <span className="text-xl mr-2">🌟</span>
                          Cosmic Message for {zodiacInfo?.zodiac_sign}
                        </h4>
                        <p className="text-gray-800 leading-relaxed font-sans text-lg whitespace-pre-line">
                          {(() => {
                            let content = '';
                            if (typeof dailyReading.content === 'string') {
                              content = dailyReading.content;
                            } else if (dailyReading.content?.message) {
                              content = dailyReading.content.message;
                            } else if (dailyReading.reading) {
                              content = dailyReading.reading;
                            } else {
                              content = JSON.stringify(dailyReading.content);
                            }
                            
                            // Remove markdown code block formatting if present
                            content = content.replace(/```json\s*/g, '').replace(/```\s*$/g, '').trim();
                            
                            // Try to parse if it looks like JSON
                            if (content.startsWith('{') || content.startsWith('[')) {
                              try {
                                const parsed = JSON.parse(content);
                                return parsed.content || parsed.message || content;
                              } catch (e) {
                                // Not valid JSON, return as is
                              }
                            }
                            
                            return content;
                          })()}
                        </p>
                      </div>

                      {/* Cosmic Affirmation */}
                      {dailyReading.affirmation && (
                        <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-6 border border-rose-200">
                          <h4 className="font-serif font-semibold text-rose-900 mb-3 flex items-center">
                            <span className="text-xl mr-2">💖</span>
                            Your Cosmic Affirmation
                          </h4>
                          <p className="text-rose-800 italic font-medium font-sans text-lg leading-relaxed">
                            "{dailyReading.affirmation}"
                          </p>
                        </div>
                      )}

                      {/* Growth Wisdom */}
                      {dailyReading.growth_tip && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                          <h4 className="font-serif font-semibold text-green-900 mb-3 flex items-center">
                            <span className="text-xl mr-2">🌱</span>
                            Growth Wisdom
                          </h4>
                          <p className="text-green-800 font-sans text-lg">
                            {dailyReading.growth_tip}
                          </p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex justify-center">
                        <Button 
                          onClick={() => saveToLoveVault(dailyReading, 'astrology')}
                          className="w-full max-w-md bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          💖 Save to Love Vault
                        </Button>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Loading State */}
                {loading && (
                  <Card className="p-8 bg-white/90 backdrop-blur-sm border-2 text-center" style={{ borderColor: '#F7C6D9' }}>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: '#F7C6D9' }}></div>
                    <h3 className="text-lg font-serif font-semibold text-gray-900 mb-2">
                      Reading the Stars...
                    </h3>
                    <p className="text-gray-600 font-sans">
                      Gathering your personalized cosmic insights ✨
                    </p>
                  </Card>
                )}
              </>
            )}
          </div>
        )}

        {/* Scripture Section */}
        {activeSection === 'scripture' && (
          <div className="space-y-6">
            
            {/* Mood Selection for Personalized Scripture */}
            <Card className="p-6 bg-white/90 backdrop-blur-sm border-2" style={{ borderColor: '#F7C6D9' }}>
              <h3 className="text-lg font-serif font-semibold text-gray-900 mb-4 text-center">
                🌸 How is your heart today?
              </h3>
              <p className="text-center text-gray-600 font-sans mb-4">
                Let us find the perfect verse to encourage your spirit
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { mood: 'peaceful', emoji: '🌙', label: 'Peaceful' },
                  { mood: 'struggling', emoji: '💔', label: 'Struggling' },
                  { mood: 'grateful', emoji: '🌸', label: 'Grateful' },
                  { mood: 'anxious', emoji: '😔', label: 'Anxious' },
                  { mood: 'hopeful', emoji: '✨', label: 'Hopeful' },
                  { mood: 'overwhelmed', emoji: '🌊', label: 'Overwhelmed' },
                  { mood: 'joyful', emoji: '🌟', label: 'Joyful' },
                  { mood: 'confused', emoji: '🤔', label: 'Confused' }
                ].map((option) => (
                  <button
                    key={option.mood}
                    onClick={() => {
                      setUserMood(option.mood);
                      // Clear previous scripture and immediately load new one
                      setScriptureGuidance(null);
                      // Trigger generation immediately after selection
                      setTimeout(() => loadScriptureGuidance(option.mood), 100);
                    }}
                    disabled={loading}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      userMood === option.mood
                        ? 'bg-rose-500 border-rose-500 text-white shadow-lg'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-rose-300 hover:bg-rose-50'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="text-2xl mb-1">{option.emoji}</div>
                    <div className="text-sm font-sans">{option.label}</div>
                  </button>
                ))}
              </div>
            </Card>

            {/* Scripture Guidance Loading */}
            {loading && (
              <Card className="p-8 bg-white/90 backdrop-blur-sm border-2" style={{ borderColor: '#F7C6D9' }}>
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-rose-500 border-t-transparent mb-4"></div>
                  <p className="text-gray-600 font-sans">Finding the perfect verse for your heart...</p>
                </div>
              </Card>
            )}

            {/* Scripture Guidance */}
            {!loading && scriptureGuidance && (
              <Card className="p-8 bg-white/90 backdrop-blur-sm border-2" style={{ borderColor: '#F7C6D9' }}>
                <div className="space-y-8">
                  
                  {/* Refresh Button */}
                  <div className="flex justify-center">
                    <button
                      onClick={async () => {
                        if (!userMood) return;
                        setLoading(true);
                        try {
                          const response = await fetch(`${API_BASE_URL}/api/scripture/guidance?mood=${userMood}`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json',
                            },
                          });

                          if (response.ok) {
                            const data = await response.json();
                            setScriptureGuidance(data);
                            
                            toast({
                              title: "📖 New Scripture Received",
                              description: `Fresh word for your ${userMood} heart`,
                              duration: 3000,
                            });
                          }
                        } catch (error) {
                          console.error('Error refreshing scripture:', error);
                        } finally {
                          setLoading(false);
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans text-sm transition-colors"
                    >
                      <Sparkles className="w-4 h-4" />
                      Get New Scripture
                    </button>
                  </div>

                  {/* Bible Verse */}
                  <div className="text-center">
                    <div className="text-4xl mb-4">📖</div>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                      <p className="text-gray-800 font-serif text-xl leading-relaxed italic">
                        {scriptureGuidance.verse}
                      </p>
                    </div>
                  </div>

                  {/* Interpretation */}
                  <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-6 border" style={{ borderColor: '#F7C6D9' }}>
                    <h4 className="font-serif font-semibold text-gray-900 mb-3 flex items-center">
                      <span className="text-xl mr-2">✨</span>
                      What This Means for You
                    </h4>
                    <p className="text-gray-800 font-sans text-lg leading-relaxed">
                      {scriptureGuidance.interpretation}
                    </p>
                  </div>

                  {/* Action Step */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                    <h4 className="font-serif font-semibold text-green-900 mb-3 flex items-center">
                      <span className="text-xl mr-2">🪞</span>
                      Today's Action Step
                    </h4>
                    <p className="text-green-800 font-sans text-lg leading-relaxed">
                      {scriptureGuidance.action_step || scriptureGuidance.actionStep || 'Reflect on this verse throughout your day and let it guide your actions.'}
                    </p>
                  </div>

                  {/* Prayer */}
                  <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6 border border-yellow-200">
                    <h4 className="font-serif font-semibold text-yellow-900 mb-3 flex items-center">
                      <span className="text-xl mr-2">🙏</span>
                      Personalized Prayer
                    </h4>
                    <p className="text-yellow-800 font-sans text-lg leading-relaxed italic">
                      {scriptureGuidance.prayer}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-center">
                    <Button 
                      onClick={() => saveToLoveVault(scriptureGuidance, 'scripture')}
                      className="w-full max-w-md bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      💖 Save to Love Vault
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Loading State for Scripture */}
            {loading && (
              <Card className="p-8 bg-white/90 backdrop-blur-sm border-2 text-center" style={{ borderColor: '#F7C6D9' }}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: '#F7C6D9' }}></div>
                <h3 className="text-lg font-serif font-semibold text-gray-900 mb-2">
                  Finding Your Scripture...
                </h3>
                <p className="text-gray-600 font-sans">
                  Selecting verses that speak to your heart today 🙏
                </p>
              </Card>
            )}
          </div>
        )}

        {/* Gentle Disclaimer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500 font-sans italic">
            ✨ Divine guidance for inspiration and spiritual growth • Always trust your heart and seek wisdom in prayer 🙏
          </p>
        </div>
      </div>
    </div>
  );
};

export default DivineGuidance;